import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShieldCheck, Store, Handshake, User, LogOut, Settings2, MapPin, ArrowRight, Eye, EyeOff, Copy, Check, Link2, Star, X } from 'lucide-react';
import { useOsTheme, ThemeToggle } from '../os3/theme';
import { wIcon } from './os2/os2ui';
import { OsFooter } from '../os3/OsFooter';
import { TextSizePill } from '../os3/OsFooter';
import { TEAM_AVATARS } from '../components/teamAvatars';
import { LangSwitch } from '../components/LangSwitch';
import AdminHome from '../os3/homes/AdminHome';
import RestaurantHome from '../os3/homes/RestaurantHome';
import AffiliateHome from '../os3/homes/AffiliateHome';
import ClientHome from '../os3/homes/ClientHome';

/* Kitxens OS Home — Role-Based Operating Centers.
   Admin, restaurant, affiliate and client each get their own tailored home. */

const API = process.env.REACT_APP_BACKEND_URL;
const ROLE_ORDER = ['admin', 'restaurant', 'affiliate', 'client'];
const ROLE_META = {
  admin: { icon: ShieldCheck, es: 'Centro de Mando', en: 'Command Center' },
  restaurant: { icon: Store, es: 'Mi Restaurante', en: 'My Restaurant' },
  affiliate: { icon: Handshake, es: 'Centro de Afiliado', en: 'Affiliate Center' },
  client: { icon: User, es: 'Mi Kitxens', en: 'My Kitxens' },
};

const AGENT_LINES = {
  rachel: {
    admin: ['Tu centro de mando está listo — revenue, leads calientes y crecimiento en Google, todo en vivo. ¿Por dónde empezamos hoy?', 'Your command center is live — revenue, hot leads and Google growth, all in real time. Where shall we start today?'],
    affiliate: ['Bienvenido a tu centro de ganancias. Comparte tu link y mira crecer tus comisiones.', 'Welcome to your earnings center. Share your link and watch your commissions grow.'],
    restaurant: ['Tu restaurante, en un vistazo. Los datos frescos te esperan dentro de tu OS.', 'Your restaurant, at a glance. Fresh data awaits inside your OS.'],
    generic: ['Bienvenido de vuelta. Todo lo tuyo está aquí, en un solo lugar.', 'Welcome back. Everything of yours is here, in one place.'],
  },
  stan: {
    admin: ['Vigilo tus números: cada pago de Stripe y cada factura quedan trazados al segundo. Sin sorpresas a fin de mes.', 'I watch your numbers: every Stripe payment and invoice is traced to the second. No month-end surprises.'],
    generic: ['Tus finanzas, ordenadas y al día. Yo me encargo de los números.', 'Your finances, tidy and current. I handle the numbers.'],
  },
  eva: {
    admin: ['Estoy analizando tus búsquedas en Google: hay consultas a un paso de la página 1. Te marco las oportunidades en Growth.', 'I am analyzing your Google queries: some are one step from page 1. Opportunities flagged in Growth.'],
    generic: ['Analizo tu presencia digital para que crezcas donde importa.', 'I analyze your digital presence so you grow where it matters.'],
  },
  sonny: {
    admin: ['El caso Casa Mia es tu mejor prueba social — publícalo y deja que venda por ti mientras duermes.', 'The Casa Mia case is your strongest social proof — publish it and let it sell for you while you sleep.'],
    generic: ['Contenido que trabaja por ti, todos los días.', 'Content that works for you, every day.'],
  },
  penny: {
    admin: ['Cada scan de Index AI es un prospecto tibio. Los que dejaron email están listos para una secuencia de venta.', 'Every Index AI scan is a warm prospect. The ones who left an email are ready for a sales sequence.'],
    generic: ['Promociones inteligentes en el momento justo.', 'Smart promotions at just the right moment.'],
  },
  linda: {
    admin: ['Operación bajo control: eventos, sincronizaciones y salud del sistema monitorizados 24/7.', 'Operations under control: events, syncs and system health monitored 24/7.'],
    generic: ['Tu operación diaria, sin fricción.', 'Your daily operation, friction-free.'],
  },
};

function HubConcierge({ role, isEs }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((v) => v + 1), 7000);
    return () => clearInterval(t);
  }, []);
  const av = TEAM_AVATARS[i % TEAM_AVATARS.length];
  const lines = AGENT_LINES[av.key] || AGENT_LINES.rachel;
  const pick = lines[role] || lines.generic;
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
      className="rounded-2xl p-5 flex items-start gap-4 h-full" style={{ background: '#17181A', border: '1px solid rgba(255,255,255,0.07)' }} data-testid="os-hub-rachel">
      <img key={av.key} src={av.img} alt={av.name} className="w-12 h-12 rounded-full object-cover border-2 border-white/20 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#F77F11]" data-testid="os-hub-agent-name">{av.name} · {av.role}</p>
        <p key={av.key + i} className="mt-1.5 text-[13.5px] text-white leading-relaxed" data-testid="os-hub-agent-msg">{pick[isEs ? 0 : 1]}</p>
        <span className="mt-2.5 flex items-center gap-1">
          {TEAM_AVATARS.map((a, di) => (
            <button key={a.key} onClick={() => setI(di)} className={`h-1 rounded-full transition-all ${di === i % TEAM_AVATARS.length ? 'w-4 bg-[#F77F11]' : 'w-1 bg-white/25'}`} aria-label={a.name} />
          ))}
        </span>
      </div>
    </motion.div>
  );
}

function BriefTile({ label, value, sub, tone = 'var(--os-ink)', delay = 0, testid, onClick, active }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`rounded-2xl p-3.5 flex flex-col justify-between os3-rise h-full text-left cursor-pointer transition-shadow hover:shadow-md ${active ? 'ring-2 ring-[#F77F11]' : ''}`}
      style={{ background: 'var(--os-surface)', border: '1px solid var(--os-border)', animationDelay: `${delay}ms` }}>
      <p className="text-[9.5px] uppercase tracking-[0.15em] font-bold flex items-center justify-between" style={{ color: 'var(--os-ink-faint)' }}>
        {label} <span className="text-[10px]">{active ? '▴' : '▾'}</span>
      </p>
      <p className="mt-1 text-[17px] font-black leading-tight tracking-tight" style={{ color: tone }}>{value}</p>
      {sub && <p className="mt-0.5 text-[10.5px] leading-snug" style={{ color: 'var(--os-ink-faint)' }}>{sub}</p>}
    </button>
  );
}

function BriefDetail({ k, admin, isEs, onEnterAdmin }) {
  const sys = admin?.system;
  let title = ''; let items = []; let rec = '';
  if (k === 'status') {
    title = isEs ? 'Servicios del sistema' : 'System services';
    items = (sys?.services || []).map((s) => ({ label: s.label, ok: s.status === 'connected' }));
    const missing = items.filter((i) => !i.ok).map((i) => i.label);
    rec = missing.length
      ? (isEs ? `Haz esto: conecta ${missing.join(', ')} desde el Admin OS para tener todo en verde.` : `Do this: connect ${missing.join(', ')} from the Admin OS to get everything green.`)
      : (isEs ? 'Todo en verde — nada que hacer aquí. ✓' : 'All green — nothing to do here. ✓');
  } else if (k === 'urgent') {
    title = isEs ? 'Qué es urgente' : "What's urgent";
    items = [
      ...(admin?.activity || []).filter((n) => ['critical', 'warning'].includes(n.severity)).slice(0, 4).map((n) => ({ label: n.title || n.message || n.type, ok: false })),
      ...(admin?.at_risk || []).slice(0, 3).map((l) => ({ label: `${isEs ? 'Lead en riesgo' : 'At-risk lead'}: ${l.email}`, ok: false })),
    ];
    rec = items.length
      ? (isEs ? 'Haz esto: responde primero las alertas críticas y escribe hoy a los leads en riesgo — se enfrían en 48h.' : 'Do this: clear the critical alerts first and write to the at-risk leads today — they go cold in 48h.')
      : (isEs ? 'Nada urgente ahora mismo. Aprovecha para hacer prospección. ✓' : 'Nothing urgent right now. Great moment for prospecting. ✓');
  } else if (k === 'today') {
    title = isEs ? 'Para hoy' : 'For today';
    items = (admin?.hot_leads || []).slice(0, 4).map((l) => ({ label: `${l.name || l.email} · score ${l.score ?? '—'}`, ok: true }));
    rec = items.length
      ? (isEs ? 'Haz esto: contacta primero al de mayor score con una propuesta corta y personal — hoy, no mañana.' : 'Do this: contact the highest score first with a short, personal pitch — today, not tomorrow.')
      : (isEs ? 'Sin leads calientes hoy — comparte el scanner Index AI para generar nuevos.' : 'No hot leads today — share the Index AI scanner to generate new ones.');
  } else if (k === 'indexai') {
    title = 'Index AI';
    const ia = admin?.index_ai;
    items = ia ? [
      { label: isEs ? `${ia.scans_week} scans esta semana` : `${ia.scans_week} scans this week`, ok: true },
      ...(ia.avg_score != null ? [{ label: isEs ? `Score medio de los últimos scans: ${ia.avg_score}/100` : `Average score of recent scans: ${ia.avg_score}/100`, ok: true }] : []),
    ] : [];
    rec = isEs
      ? `Haz esto: cada scan es un prospecto tibio. Comparte el scanner con 3 restaurantes esta semana — de media hay un +${ia?.improvement_pct ?? '—'}% de mejora posible que puedes venderles.`
      : `Do this: every scan is a warm prospect. Share the scanner with 3 restaurants this week — on average there's +${ia?.improvement_pct ?? '—'}% possible lift you can sell them.`;
  }
  return (
    <div className="mt-2 rounded-2xl px-4 py-3.5" data-testid={`hub-brief-detail-${k}`}
      style={{ background: 'var(--os-surface)', border: '1px solid var(--os-border)' }}>
      <p className="text-[10px] uppercase tracking-[0.15em] font-bold" style={{ color: '#F77F11' }}>{title}</p>
      {!!items.length && (
        <ul className="mt-2 space-y-1.5">
          {items.map((it, i) => (
            <li key={i} className="flex items-start gap-2 text-[12.5px]" style={{ color: 'var(--os-ink-soft)' }}>
              <span className="mt-1 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: it.ok ? '#10B981' : '#F59E0B' }} />
              <span>{it.label}</span>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-2.5 text-[12px] font-semibold" style={{ color: 'var(--os-ink)' }}>💡 {rec}</p>
      {onEnterAdmin && (
        <button onClick={onEnterAdmin} data-testid={`hub-brief-open-${k}`}
          className="mt-2 inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-[12px] font-bold text-white transition-opacity hover:opacity-90" style={{ background: '#F77F11' }}>
          {isEs ? 'Abrir Admin OS →' : 'Open Admin OS →'}
        </button>
      )}
    </div>
  );
}

function AdminBriefRow({ admin, isEs, sel, setSel }) {
  if (!admin) return null;
  const sys = admin.system;
  const okServices = sys ? sys.services.filter((s) => s.status === 'connected').length : 0;
  const urgent = (sys?.incidents || 0) + (sys?.dlq || 0) + (admin.at_risk || []).length;
  const hot = (admin.hot_leads || []).length;
  const ia = admin.index_ai;
  const pick = (k) => () => setSel(sel === k ? null : k);
  return (
    <>
      <BriefTile delay={60} testid="hub-brief-status" onClick={pick('status')} active={sel === 'status'}
        label={isEs ? 'Estado' : 'Status'}
        value={sys ? (okServices === sys.services.length ? (isEs ? 'Operativo ✓' : 'Operational ✓') : `${okServices}/${sys.services.length}`) : '—'}
        tone={sys && okServices === sys.services.length ? '#10B981' : '#F59E0B'}
        sub={sys ? (isEs ? `${sys.events_today} eventos hoy` : `${sys.events_today} events today`) : ''} />
      <BriefTile delay={120} testid="hub-brief-urgent" onClick={pick('urgent')} active={sel === 'urgent'}
        label={isEs ? 'Urgente' : 'Urgent'}
        value={urgent ? urgent : (isEs ? 'Nada ✓' : 'None ✓')}
        tone={urgent ? '#F59E0B' : '#10B981'}
        sub={urgent ? (isEs ? 'incidencias + leads en riesgo' : 'incidents + at-risk leads') : (isEs ? 'todo bajo control' : 'all under control')} />
      <BriefTile delay={180} testid="hub-brief-today" onClick={pick('today')} active={sel === 'today'}
        label={isEs ? 'Hoy' : 'Today'}
        value={hot ? `${hot} ${isEs ? 'leads 🔥' : 'hot leads'}` : money(admin.revenue_today || 0)}
        tone="var(--os-ink)"
        sub={hot ? (isEs ? 'contactar prioritariamente' : 'contact first') : (isEs ? 'revenue de hoy' : "today's revenue")} />
      <BriefTile delay={240} testid="hub-brief-indexai" onClick={pick('indexai')} active={sel === 'indexai'}
        label="Index AI"
        value={ia?.improvement_pct != null ? `+${ia.improvement_pct}%` : '—'}
        tone="#F77F11"
        sub={ia ? (isEs ? `mejora posible en búsqueda · ${ia.scans_week} scans/sem` : `possible search lift · ${ia.scans_week} scans/wk`) : ''} />
    </>
  );
}

const money = (v) => `$${(Number(v) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

function VipRadar({ isEs }) {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const load = async () => {
    try {
      const r = await fetch(`${API}/api/demo-access/vip-stats`, { credentials: 'include' });
      if (!r.ok) throw new Error();
      setRows((await r.json()).invites || []);
    } catch { setRows([]); }
  };
  useEffect(() => { if (open && rows === null) load(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const ago = (iso) => {
    if (!iso) return isEs ? 'nunca' : 'never';
    const h = Math.max(0, (Date.now() - new Date(iso).getTime()) / 36e5);
    if (h < 1) return isEs ? 'hace minutos' : 'minutes ago';
    if (h < 24) return isEs ? `hace ${Math.round(h)}h` : `${Math.round(h)}h ago`;
    return isEs ? `hace ${Math.round(h / 24)}d` : `${Math.round(h / 24)}d ago`;
  };
  return (
    <div className="mt-4 pt-4" style={{ borderTop: '1px dashed var(--os-border)' }} data-testid="hub-vip-radar">
      <div className="flex items-center justify-between">
        <button onClick={() => setOpen((o) => !o)} data-testid="hub-vip-radar-toggle"
          className="text-[13px] font-bold hover:opacity-80 transition-opacity" style={{ color: 'var(--os-ink)' }}>
          📡 {isEs ? 'Radar VIP — quién entró con cada enlace' : 'VIP Radar — who came in with each link'} {open ? '▾' : '▸'}
        </button>
        {open && (
          <button onClick={load} data-testid="hub-vip-radar-refresh"
            className="text-[11px] font-bold uppercase tracking-wide text-[#F77F11] hover:opacity-80">
            {isEs ? 'Actualizar' : 'Refresh'}
          </button>
        )}
      </div>
      {open && rows === null && <p className="mt-2 text-[12.5px]" style={{ color: 'var(--os-ink-faint)' }}>…</p>}
      {open && rows !== null && rows.length === 0 && (
        <p className="mt-2 text-[12.5px]" style={{ color: 'var(--os-ink-faint)' }}>
          {isEs ? 'Aún no hay enlaces VIP creados.' : 'No VIP links created yet.'}
        </p>
      )}
      {open && rows !== null && rows.length > 0 && (
        <div className="mt-2 space-y-1.5 max-h-72 overflow-y-auto pr-1">
          {rows.map((r) => (
            <div key={r.code} className="rounded-xl px-3.5 py-2.5" data-testid={`hub-vip-radar-row-${r.code}`}
              style={{ background: 'var(--os-bg)', border: '1px solid var(--os-border)' }}>
              <div className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-[12.5px] font-semibold truncate" style={{ color: 'var(--os-ink)' }}>
                  {r.source === 'self' ? '💌 ' : '🎟️ '}{r.label || r.email || r.code}
                  {r.emailed_at && <span className="ml-1.5 text-[10px] font-bold text-emerald-600">{isEs ? '· email enviado' : '· emailed'}</span>}
                </p>
                <a href={r.link} target="_blank" rel="noreferrer"
                  className="text-[10.5px] font-mono truncate block underline decoration-dotted underline-offset-2 hover:text-[#F77F11] transition-colors"
                  style={{ color: 'var(--os-ink-faint)' }} data-testid={`hub-vip-radar-open-${r.code}`}>{r.link}</a>
              </div>
              <div className="shrink-0 text-right">
                <p className="text-[12.5px] font-bold" style={{ color: r.uses > 0 ? '#F77F11' : 'var(--os-ink-faint)' }}>
                  {r.uses} {isEs ? (r.uses === 1 ? 'visita' : 'visitas') : (r.uses === 1 ? 'visit' : 'visits')}
                  {r.contacts > 0 && <span className="ml-1.5 text-emerald-600">· {r.contacts} 📧</span>}
                </p>
                <p className="text-[10.5px]" style={{ color: 'var(--os-ink-faint)' }}>{isEs ? 'última: ' : 'last: '}{ago(r.last_used_at)}</p>
              </div>
              <button onClick={() => navigator.clipboard.writeText(r.link).catch(() => {})}
                data-testid={`hub-vip-radar-copy-${r.code}`}
                className="shrink-0 text-[10px] font-bold uppercase tracking-wide text-[#F77F11] hover:opacity-80">
                {isEs ? 'Copiar' : 'Copy'}
              </button>
              </div>
              {(r.contact_emails || []).length > 0 && (
                <div className="mt-1.5 pt-1.5 flex flex-wrap items-center gap-1.5" style={{ borderTop: '1px dashed var(--os-border)' }}
                  data-testid={`hub-vip-radar-emails-${r.code}`}>
                  <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: 'var(--os-ink-faint)' }}>
                    {isEs ? 'Dejaron su email:' : 'Left their email:'}
                  </span>
                  {r.contact_emails.map((em) => (
                    <a key={em} href={`mailto:${em}`}
                      className="text-[11px] font-semibold text-emerald-600 hover:underline">{em}</a>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const SITE = 'https://kitxens.com';
const LINK_GROUPS = (isEs) => [
  { key: 'clients', title: isEs ? 'Así nos ven los clientes' : 'How clients see us', items: [
    [isEs ? 'Página principal' : 'Homepage', '/'],
    [isEs ? 'Index AI™ — scan gratis (lead magnet)' : 'Index AI™ — free scan (lead magnet)', '/index-ai'],
    [isEs ? 'Experiencia demo (Rachel)' : 'Demo experience (Rachel)', '/demo'],
    ['Kitxens Magazine', '/magazine'],
    [isEs ? 'Precios' : 'Pricing', '/pricing'],
  ] },
  { key: 'capture', title: isEs ? 'Toma de datos (leads → Brevo CRM)' : 'Data capture (leads → Brevo CRM)', items: [
    [isEs ? 'Auditoría gratis' : 'Free growth audit', '/free-audit'],
    [isEs ? 'Contacto' : 'Contact', '/contact'],
    [isEs ? 'Reservar demo con el equipo' : 'Book a demo with the team', '/demo/book'],
  ] },
  { key: 'partners', title: 'Partners', items: [
    [isEs ? 'Programa de partners' : 'Partner program', '/partners'],
    [isEs ? 'Panel de afiliado' : 'Affiliate dashboard', '/affiliates/dashboard'],
    [isEs ? 'Acceso Kitxens OS' : 'Kitxens OS access', '/hub'],
  ] },
];

function KeyLinksCard({ isEs }) {
  const [copied, setCopied] = useState('');
  const copy = async (path) => {
    try { await navigator.clipboard.writeText(`${SITE}${path === '/' ? '' : path}`); setCopied(path); setTimeout(() => setCopied(''), 1600); } catch { /* noop */ }
  };
  return (
    <div className="mt-4 rounded-2xl p-5" data-testid="hub-key-links"
      style={{ background: 'var(--os-surface)', border: '1px solid var(--os-border)' }}>
      <p className="text-[11px] uppercase tracking-[0.15em] font-bold flex items-center gap-1.5" style={{ color: 'var(--os-ink-faint)' }}>
        <Link2 className="w-3.5 h-3.5" style={{ color: 'var(--os-accent)' }} aria-hidden />
        {isEs ? 'Enlaces clave · para compartir' : 'Key links · ready to share'}
      </p>
      <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-4">
        {LINK_GROUPS(isEs).map((g) => (
          <div key={g.key} data-testid={`hub-key-links-${g.key}`}>
            <p className="text-[12px] font-bold mb-2" style={{ color: 'var(--os-accent)' }}>{g.title}</p>
            <div className="space-y-1.5">
              {g.items.map(([label, path]) => (
                <div key={path + label} className="flex items-center gap-2 rounded-lg px-2.5 py-1.5"
                  style={{ background: 'var(--os-surface-2)', border: '1px solid var(--os-border)' }}>
                  <a href={`${SITE}${path === '/' ? '' : path}`} target="_blank" rel="noreferrer"
                    className="min-w-0 flex-1 group" data-testid={`hub-key-link-${g.key}-${path.replace(/\//g, '-') || 'home'}`}>
                    <p className="text-[12.5px] font-semibold truncate group-hover:underline underline-offset-2" style={{ color: 'var(--os-ink)' }}>{label}</p>
                    <p className="text-[11px] truncate" style={{ color: 'var(--os-ink-faint)' }}>kitxens.com{path === '/' ? '' : path}</p>
                  </a>
                  <button onClick={() => copy(path)} aria-label="Copy link"
                    data-testid={`hub-key-copy-${g.key}-${path.replace(/\//g, '-') || 'home'}`}
                    className="w-7 h-7 shrink-0 rounded-full flex items-center justify-center transition-colors hover:opacity-70"
                    style={{ color: copied === path ? '#22c55e' : 'var(--os-ink-faint)' }}>
                    {copied === path ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function VipInviteCard({ isEs }) {
  const [label, setLabel] = useState('');
  const [email, setEmail] = useState('');
  const [inv, setInv] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [emailErr, setEmailErr] = useState(false);
  const create = async () => {
    if (busy) return;
    const em = email.trim().toLowerCase();
    if (em && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(em)) { setEmailErr(true); return; }
    setBusy(true); setEmailErr(false);
    try {
      const r = await fetch(`${API}/api/demo-access/vip-invite`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ label: label.trim(), email: em, locale: isEs ? 'es' : 'en' }) });
      if (!r.ok) throw new Error();
      const d = await r.json();
      setInv({ ...d, sent_to: em });
      try { await navigator.clipboard.writeText(d.link); setCopied(true); setTimeout(() => setCopied(false), 2500); } catch { /* noop */ }
    } catch { /* noop */ } finally { setBusy(false); }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(inv.link); setCopied(true); setTimeout(() => setCopied(false), 2500); } catch { /* noop */ }
  };
  return (
    <div className="mt-8 rounded-2xl p-5" data-testid="hub-vip-invite"
      style={{ background: 'var(--os-surface)', border: '1px solid var(--os-border)', borderTop: '3px solid #F77F11' }}>
      <p className="text-[11px] uppercase tracking-[0.15em] font-bold" style={{ color: 'var(--os-ink-faint)' }}>
        {isEs ? 'Demo VIP · sin registro' : 'VIP Demo · no registration'}
      </p>
      <p className="mt-1.5 text-[13.5px]" style={{ color: 'var(--os-ink-soft)' }}>
        {isEs ? 'Invita a clientes, socios, inversores o amigos: entran directos a la demo, sin formularios. Con email: Rachel les envía su enlace permanente y quedan en el CRM.'
          : 'Invite clients, partners, investors or friends: they walk straight into the demo, no forms. With an email: Rachel mails them their permanent link and they land in the CRM.'}
      </p>
      <div className="mt-3 flex flex-col sm:flex-row gap-2">
        <input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={80}
          placeholder={isEs ? 'Para quién es (ej. "Inversores Q3")' : 'Who it\'s for (e.g. "Q3 investors")'}
          data-testid="hub-vip-label"
          className="flex-1 rounded-xl px-4 py-2.5 text-[13.5px] outline-none"
          style={{ background: 'var(--os-bg)', border: '1px solid var(--os-border)', color: 'var(--os-ink)' }} />
        <input value={email} onChange={(e) => { setEmail(e.target.value); setEmailErr(false); }} maxLength={200} type="email"
          placeholder={isEs ? 'Email del invitado (opcional)' : "Guest's email (optional)"}
          data-testid="hub-vip-email"
          className="flex-1 rounded-xl px-4 py-2.5 text-[13.5px] outline-none"
          style={{ background: 'var(--os-bg)', border: `1px solid ${emailErr ? '#EF4444' : 'var(--os-border)'}`, color: 'var(--os-ink)' }} />
        <button onClick={create} disabled={busy} data-testid="hub-vip-create"
          className="rounded-xl px-5 py-2.5 text-[13px] font-bold text-white bg-[#F77F11] hover:brightness-110 transition-[filter] disabled:opacity-60">
          {busy ? (isEs ? 'Creando…' : 'Creating…') : (isEs ? 'Crear link VIP' : 'Create VIP link')}
        </button>
      </div>
      {emailErr && <p className="mt-2 text-[12px] text-red-500" data-testid="hub-vip-email-error">{isEs ? 'Revisa el email del invitado.' : "Check the guest's email."}</p>}
      {inv && (
        <>
          {inv.already_known && (
            <p className="mt-3 text-[12.5px] font-semibold text-[#F77F11]" data-testid="hub-vip-known">
              {isEs
                ? `👋 Ya ${inv.known_name ? `conocemos a ${inv.known_name}` : 'conocemos este email'}${inv.known_stage ? ` (etapa: ${inv.known_stage})` : ''} — este es su enlace de siempre, no se creó otro.`
                : `👋 We already ${inv.known_name ? `know ${inv.known_name}` : 'know this email'}${inv.known_stage ? ` (stage: ${inv.known_stage})` : ''} — this is their usual link, no duplicate was created.`}
            </p>
          )}
          <div className="mt-3 flex items-center gap-2 rounded-xl px-4 py-2.5" data-testid="hub-vip-link"
            style={{ background: 'var(--os-bg)', border: '1px solid var(--os-border)' }}>
            <a href={inv.link} target="_blank" rel="noreferrer" data-testid="hub-vip-link-open"
              className="flex-1 text-[12.5px] font-mono truncate underline decoration-dotted underline-offset-2 hover:text-[#F77F11] transition-colors"
              style={{ color: 'var(--os-ink)' }}>{inv.link}</a>
            <button onClick={copy} data-testid="hub-vip-copy"
              className="text-[11px] font-bold uppercase tracking-wide text-[#F77F11] hover:opacity-80 shrink-0">
              {copied ? (isEs ? '¡Copiado!' : 'Copied!') : (isEs ? 'Copiar' : 'Copy')}
            </button>
          </div>
          {inv.sent_to && (
            <p className="mt-2 text-[12.5px] font-semibold" data-testid="hub-vip-emailed"
              style={{ color: inv.emailed ? '#059669' : 'var(--os-ink-faint)' }}>
              {inv.emailed
                ? (isEs ? `✓ Email enviado a ${inv.sent_to} y contacto añadido al CRM (Brevo).` : `✓ Email sent to ${inv.sent_to} and contact added to the CRM (Brevo).`)
                : (isEs ? `Contacto añadido al CRM — el email no pudo enviarse, comparte el link manualmente.` : `Contact added to the CRM — the email couldn't be sent, share the link manually.`)}
            </p>
          )}
        </>
      )}
      <VipRadar isEs={isEs} />
    </div>
  );
}

const DED_VOICES = [
  ['onyx', 'Alejandro F. · voz profunda'],
  ['coral', 'Scarlett J. · cálida'],
  ['shimmer', 'Anne H. · luminosa'],
  ['sage', 'Natalie P. · serena'],
  ['nova', 'Jennifer L. · vibrante'],
];

function DedicationCard({ isEs }) {
  const [open, setOpen] = useState(false);
  const [toName, setToName] = useState('');
  const [message, setMessage] = useState('');
  const [voice, setVoice] = useState('onyx');
  const [sign, setSign] = useState('');
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState('');
  const [list, setList] = useState([]);
  const [newOpens, setNewOpens] = useState(0);
  const load = async () => {
    try {
      const r = await fetch(`${API}/api/demo-access/dedications`, { credentials: 'include' });
      if (r.ok) {
        const rows = (await r.json()).dedications || [];
        setList(rows);
        setNewOpens(rows.reduce((n, d) => n + (d.new_opens || 0), 0));
      }
    } catch { /* noop */ }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!open || !newOpens) return undefined;
    // She opened it — only you saw. After a quiet look, the badge rests.
    const t = setTimeout(async () => {
      try {
        await fetch(`${API}/api/demo-access/dedication/ack`, { method: 'POST', credentials: 'include' });
        setNewOpens(0);
      } catch { /* noop */ }
    }, 4000);
    return () => clearTimeout(t);
  }, [open, newOpens]);
  const previewRef = React.useRef(null);
  const [previewing, setPreviewing] = useState(false);
  useEffect(() => () => { try { previewRef.current?.pause(); } catch { /* noop */ } }, []);
  const preview = async () => {
    if (previewing || !message.trim()) return;
    setPreviewing(true);
    try {
      previewRef.current?.pause();
      const text = `${toName.trim() || (isEs ? 'Para ti' : 'For you')}... ${message.trim()}`.slice(0, 600);
      const r = await fetch(`${API}/api/rest/present/voice`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, lang: 'es', voice }) });
      if (!r.ok) throw new Error();
      const url = URL.createObjectURL(await r.blob());
      const a = new Audio(url);
      previewRef.current = a;
      a.onended = () => { setPreviewing(false); URL.revokeObjectURL(url); };
      a.onerror = () => { setPreviewing(false); URL.revokeObjectURL(url); };
      await a.play();
    } catch { setPreviewing(false); }
  };
  const copy = async (link, key) => {
    try { await navigator.clipboard.writeText(link); setCopied(key); setTimeout(() => setCopied(''), 2500); } catch { /* noop */ }
  };
  const create = async () => {
    if (busy || !toName.trim() || !message.trim()) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/demo-access/dedication`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to_name: toName.trim(), message: message.trim(), voice, signature: sign.trim(), locale: 'es' }) });
      if (!r.ok) throw new Error();
      const d = await r.json();
      setCreated(d);
      copy(d.link, 'new');
      load();
    } catch { /* noop */ } finally { setBusy(false); }
  };
  const revoke = async (code) => {
    try {
      await fetch(`${API}/api/demo-access/dedication/revoke`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }) });
      load();
    } catch { /* noop */ }
  };
  const inputStyle = { background: 'var(--os-bg)', border: '1px solid var(--os-border)', color: 'var(--os-ink)' };
  return (
    <div className="mt-8 rounded-2xl p-5" data-testid="hub-dedication-card"
      style={{ background: 'var(--os-surface)', border: '1px solid var(--os-border)', borderTop: '3px solid #E11D48' }}>
      <button onClick={() => setOpen((o) => !o)} data-testid="hub-dedication-toggle"
        className="w-full flex items-center justify-between text-left">
        <span className="text-[11px] uppercase tracking-[0.15em] font-bold" style={{ color: 'var(--os-ink-faint)' }}>
          ❤ {isEs ? 'Dedicatorias privadas · solo tú tienes el enlace' : 'Private dedications · only you hold the link'} {open ? '▾' : '▸'}
        </span>
        {newOpens > 0 && (
          <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10.5px] font-bold text-white bg-[#E11D48]"
            data-testid="hub-dedication-new-opens" style={{ animation: 'pulse 2s ease-in-out infinite' }}>
            ● {newOpens} {isEs ? (newOpens === 1 ? 'apertura nueva' : 'aperturas nuevas') : (newOpens === 1 ? 'new open' : 'new opens')}
          </span>
        )}
      </button>
      {open && (
        <>
          <p className="mt-2 text-[13px]" style={{ color: 'var(--os-ink-soft)' }}>
            {isEs
              ? 'Crea un enlace secreto con tu propio mensaje y voz. No aparece en el radar VIP, no va al CRM ni al AI Team — solo existe para quien tú se lo compartas. Puedes revocarlo cuando quieras.'
              : 'Create a secret link with your own message and voice. It never shows in the VIP radar, never goes to the CRM or the AI Team — it only exists for whoever you share it with. Revoke it anytime.'}
          </p>
          <div className="mt-3 flex flex-col gap-2">
            <div className="flex flex-col sm:flex-row gap-2">
              <input value={toName} onChange={(e) => setToName(e.target.value)} maxLength={60}
                placeholder={isEs ? 'Para quién (ej. "Yerika")' : 'For whom (e.g. "Yerika")'}
                data-testid="hub-dedication-name"
                className="flex-1 rounded-xl px-4 py-2.5 text-[13.5px] outline-none" style={inputStyle} />
              <input value={sign} onChange={(e) => setSign(e.target.value)} maxLength={80}
                placeholder={isEs ? 'Firma (opcional, ej. "Alejandro")' : 'Signature (optional)'}
                data-testid="hub-dedication-sign"
                className="flex-1 rounded-xl px-4 py-2.5 text-[13.5px] outline-none" style={inputStyle} />
              <select value={voice} onChange={(e) => setVoice(e.target.value)} data-testid="hub-dedication-voice"
                className="rounded-xl px-3 py-2.5 text-[13px] outline-none" style={inputStyle}>
                {DED_VOICES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} maxLength={600} rows={3} data-clarity-mask="true"
              placeholder={isEs ? 'Tu mensaje personal — se dirá con la voz elegida antes de empezar la experiencia…' : 'Your personal message — spoken in the chosen voice before the experience begins…'}
              data-testid="hub-dedication-message"
              className="rounded-xl px-4 py-2.5 text-[13.5px] outline-none resize-none" style={inputStyle} />
            <div className="flex items-center gap-2">
              <button onClick={create} disabled={busy || !toName.trim() || !message.trim()} data-testid="hub-dedication-create"
                className="rounded-xl px-5 py-2.5 text-[13px] font-bold text-white bg-[#E11D48] hover:brightness-110 transition-[filter] disabled:opacity-50">
                {busy ? (isEs ? 'Creando…' : 'Creating…') : (isEs ? 'Crear enlace dedicado' : 'Create dedicated link')}
              </button>
              <button onClick={preview} disabled={previewing || !message.trim()} data-testid="hub-dedication-preview"
                className="rounded-xl px-5 py-2.5 text-[13px] font-bold transition-[filter] hover:brightness-110 disabled:opacity-50"
                style={{ background: 'var(--os-bg)', border: '1px solid var(--os-border)', color: 'var(--os-ink)' }}>
                {previewing ? (isEs ? '🔊 Sonando…' : '🔊 Playing…') : (isEs ? '▶ Escuchar antes' : '▶ Listen first')}
              </button>
            </div>
          </div>
          {created && (
            <div className="mt-3 flex items-center gap-2 rounded-xl px-4 py-2.5" data-testid="hub-dedication-link"
              style={{ background: 'var(--os-bg)', border: '1px solid var(--os-border)' }}>
              <a href={created.link} target="_blank" rel="noreferrer" data-testid="hub-dedication-link-open"
                className="flex-1 text-[12.5px] font-mono truncate underline decoration-dotted underline-offset-2 hover:text-[#E11D48] transition-colors"
                style={{ color: 'var(--os-ink)' }}>{created.link}</a>
              <button onClick={() => copy(created.link, 'new')} data-testid="hub-dedication-copy"
                className="text-[11px] font-bold uppercase tracking-wide text-[#E11D48] hover:opacity-80 shrink-0">
                {copied === 'new' ? (isEs ? '¡Copiado!' : 'Copied!') : (isEs ? 'Copiar' : 'Copy')}
              </button>
            </div>
          )}
          {list.length > 0 && (
            <div className="mt-4 flex flex-col gap-2" data-testid="hub-dedication-list">
              {list.map((d) => (
                <div key={d.code} className="rounded-xl px-3.5 py-2.5 flex flex-wrap items-center gap-x-3 gap-y-1"
                  data-testid={`hub-dedication-row-${d.code}`}
                  style={{ background: 'var(--os-bg)', border: '1px solid var(--os-border)', opacity: d.revoked_at ? 0.5 : 1 }}>
                  <span className="text-[13px] font-bold" style={{ color: 'var(--os-ink)' }}>❤ {d.to_name}</span>
                  {(d.new_opens || 0) > 0 && (
                    <span className="rounded-full px-2 py-0.5 text-[10px] font-bold text-white bg-[#E11D48]" data-testid={`hub-dedication-newopen-${d.code}`}>
                      {isEs ? `¡La abrió! +${d.new_opens}` : `Opened! +${d.new_opens}`}
                    </span>
                  )}
                  <span className="text-[11.5px]" style={{ color: 'var(--os-ink-faint)' }}>
                    {d.revoked_at
                      ? (isEs ? 'Revocado' : 'Revoked')
                      : `${d.uses || 0} ${isEs ? 'visitas' : 'visits'}${d.last_used_at ? ` · ${isEs ? 'última' : 'last'}: ${String(d.last_used_at).slice(0, 16).replace('T', ' ')}` : ''}`}
                  </span>
                  <span className="flex-1" />
                  {!d.revoked_at && (
                    <>
                      <button onClick={() => copy(d.link, d.code)} data-testid={`hub-dedication-copy-${d.code}`}
                        className="text-[11px] font-bold uppercase tracking-wide text-[#E11D48] hover:opacity-80">
                        {copied === d.code ? (isEs ? '¡Copiado!' : 'Copied!') : (isEs ? 'Copiar' : 'Copy')}
                      </button>
                      <button onClick={() => revoke(d.code)} data-testid={`hub-dedication-revoke-${d.code}`}
                        className="text-[11px] font-bold uppercase tracking-wide hover:opacity-80" style={{ color: 'var(--os-ink-faint)' }}>
                        {isEs ? 'Revocar' : 'Revoke'}
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

const AWAY_LABELS = {
  interrupted: ['Me interrumpieron', 'Got interrupted'],
  browsing: ['Solo curioseaba', 'Just browsing'],
  later: ['Lo verá después', 'Will watch later'],
};

function AwayRadar({ isEs }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const load = async () => {
    try {
      const r = await fetch(`${API}/api/demo-access/away-reasons`, { credentials: 'include' });
      if (r.ok) setData(await r.json());
    } catch { /* noop */ }
  };
  useEffect(() => { if (open && !data) load(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const label = (k) => (AWAY_LABELS[k] ? AWAY_LABELS[k][isEs ? 0 : 1] : k);
  const max = data ? Math.max(1, ...Object.values(data.counts || {})) : 1;
  return (
    <div className="mt-8 rounded-2xl p-5" data-testid="hub-away-radar"
      style={{ background: 'var(--os-surface)', border: '1px solid var(--os-border)', borderTop: '3px solid #6366F1' }}>
      <button onClick={() => setOpen((o) => !o)} data-testid="hub-away-radar-toggle"
        className="w-full flex items-center justify-between text-left">
        <span className="text-[11px] uppercase tracking-[0.15em] font-bold" style={{ color: 'var(--os-ink-faint)' }}>
          🚪 {isEs ? 'Radar de abandono · por qué se van a mitad de la demo' : 'Abandonment radar · why they leave the demo mid-way'} {open ? '▾' : '▸'}
        </span>
        {open && (
          <button onClick={(e) => { e.stopPropagation(); setData(null); load(); }} data-testid="hub-away-radar-refresh"
            className="text-[10.5px] font-bold uppercase tracking-wide text-[#6366F1] hover:opacity-80">
            {isEs ? 'Actualizar' : 'Refresh'}
          </button>
        )}
      </button>
      {open && !data && <p className="mt-3 text-[12.5px]" style={{ color: 'var(--os-ink-faint)' }}>{isEs ? 'Cargando…' : 'Loading…'}</p>}
      {open && data && data.total === 0 && (
        <p className="mt-3 text-[12.5px]" style={{ color: 'var(--os-ink-faint)' }}>
          {isEs ? 'Aún no hay motivos registrados — la demo pregunta al volver de una pausa.' : 'No reasons recorded yet — the demo asks when visitors come back from a pause.'}
        </p>
      )}
      {open && data && data.total > 0 && (
        <div className="mt-4">
          <p className="text-[12px] font-semibold" style={{ color: 'var(--os-ink-soft)' }} data-testid="hub-away-radar-total">
            {data.total} {isEs ? 'respuestas en total' : 'answers in total'}
          </p>
          <div className="mt-3 flex flex-col gap-2">
            {Object.entries(data.counts).sort((a, b) => b[1] - a[1]).map(([k, n]) => (
              <div key={k} className="flex items-center gap-3" data-testid={`hub-away-radar-row-${k}`}>
                <span className="w-40 shrink-0 text-[12.5px] font-semibold" style={{ color: 'var(--os-ink)' }}>{label(k)}</span>
                <div className="flex-1 h-2.5 rounded-full overflow-hidden" style={{ background: 'var(--os-bg)' }}>
                  <div className="h-full rounded-full bg-[#6366F1]" style={{ width: `${Math.round((n / max) * 100)}%`, transition: 'width 0.6s ease' }} />
                </div>
                <span className="w-8 text-right text-[12.5px] font-bold" style={{ color: 'var(--os-ink)' }}>{n}</span>
              </div>
            ))}
          </div>
          {(data.recent || []).length > 0 && (
            <div className="mt-4 pt-3" style={{ borderTop: '1px dashed var(--os-border)' }}>
              <p className="text-[10.5px] uppercase tracking-[0.15em] font-bold" style={{ color: 'var(--os-ink-faint)' }}>
                {isEs ? 'Últimos' : 'Latest'}
              </p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {data.recent.slice(0, 10).map((r, i) => (
                  <span key={i} className="rounded-full px-2.5 py-1 text-[11px]"
                    style={{ background: 'var(--os-bg)', border: '1px solid var(--os-border)', color: 'var(--os-ink-soft)' }}>
                    {label(r.reason)} · {String(r.created_at || '').slice(5, 16).replace('T', ' ')}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function OsHubPage() {
  const nav = useNavigate();
  const isEs = (localStorage.getItem('kitxens-lang') || 'en') === 'es';
  const [theme, setTheme] = useOsTheme();
  const [me, setMe] = useState(null);
  const [home, setHome] = useState(null);
  const [role, setRole] = useState('');
  const [err, setErr] = useState('');
  const [kwOpen, setKwOpen] = useState(false);
  const [briefSel, setBriefSel] = useState(null);
  const [kw, setKw] = useState('');
  const [kwErr, setKwErr] = useState('');
  const [kwSent, setKwSent] = useState('');
  const [showKw, setShowKw] = useState(false);
  const [busy, setBusy] = useState('');

  useEffect(() => {
    Promise.all([
      fetch(`${API}/api/os/me`, { credentials: 'include' }).then((r) => (r.ok ? r.json() : Promise.reject())),
      fetch(`${API}/api/os/home`, { credentials: 'include' }).then((r) => (r.ok ? r.json() : {})),
    ])
      .then(([m, h]) => {
        setMe(m); setHome(h);
        const roles = ROLE_ORDER.filter((r) => (h.roles || []).includes(r))
          .filter((r, _, all) => !(r === 'restaurant' && all.includes('admin')));
        setRole(roles[0] || '');
      })
      .catch(() => nav('/login', { replace: true }));
  }, [nav]);

  const enter = async (w, keyword = '') => {
    try { localStorage.setItem('kx_last_ws', w.id); } catch { /* noop */ }
    setBusy(w.id); setErr('');
    try {
      const r = await fetch(`${API}/api/os/enter`, { method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ workspace_id: w.id, keyword }) });
      const j = await r.json();
      if (!r.ok) {
        if (j.detail === 'keyword_required') { setKwErr(''); setKwOpen(true); return; }
        if (j.detail === 'keyword_invalid') { setKwErr('invalid'); setKwOpen(true); return; }
        if (kwOpen) { setKwErr(typeof j.detail === 'string' ? j.detail : 'error'); return; }
        setErr(typeof j.detail === 'string' ? j.detail : 'error'); return;
      }
      window.location.href = j.path;
    } catch { setErr('network'); } finally { setBusy(''); }
  };

  const lastWs = (() => { try { return localStorage.getItem('kx_last_ws'); } catch { return null; } })();

  const dragId = React.useRef(null);
  const onDragStartWs = (e, w) => { dragId.current = w.id; e.dataTransfer.effectAllowed = 'move'; };
  const onDropWs = async (e, target) => {
    e.preventDefault();
    const src = dragId.current; dragId.current = null;
    if (!src || src === target.id) return;
    const ids = me.workspaces.map((x) => x.id);
    const from = ids.indexOf(src); const to = ids.indexOf(target.id);
    if (from < 0 || to < 0) return;
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    const byId = Object.fromEntries(me.workspaces.map((x) => [x.id, x]));
    setMe((m) => ({ ...m, workspaces: ids.map((wid) => byId[wid]) }));
    await fetch(`${API}/api/os/workspaces/order`, { method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ids }) });
  };

  const hideWs = async (e, w) => {
    e.stopPropagation(); e.preventDefault();
    if (!window.confirm(isEs ? `¿Ocultar "${w.name}" de tu hub?` : `Hide "${w.name}" from your hub?`)) return;
    await fetch(`${API}/api/os/workspaces/hide`, { method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ workspace_id: w.id }) });
    setMe((m) => ({ ...m, hidden_count: (m.hidden_count || 0) + 1, workspaces: m.workspaces.filter((x) => x.id !== w.id) }));
  };
  const unhideAll = async () => {
    await fetch(`${API}/api/os/workspaces/unhide-all`, { method: 'POST', credentials: 'include' });
    window.location.reload();
  };

  const logout = async () => {
    await fetch(`${API}/api/os/logout`, { method: 'POST', credentials: 'include' });
    nav('/login');
  };

  if (!me) {
    return (
      <div className="min-h-screen os3-scope flex items-center justify-center" style={{ background: 'var(--os-bg)' }}>
        <p className="text-sm" style={{ color: 'var(--os-ink-faint)' }}>Kitxens OS…</p>
      </div>
    );
  }

  const roles = ROLE_ORDER.filter((r) => (home?.roles || []).includes(r))
    .filter((r, _, all) => !(r === 'restaurant' && all.includes('admin')));
  const h = new Date().getHours();
  const greet = h < 12 ? (isEs ? 'Buenos días' : 'Good morning') : h < 18 ? (isEs ? 'Buenas tardes' : 'Good afternoon') : (isEs ? 'Buenas noches' : 'Good evening');
  const meta = ROLE_META[role] || ROLE_META.client;

  return (
    <div className="min-h-screen os3-scope" style={{ background: 'var(--os-bg)', color: 'var(--os-ink-soft)', fontFamily: 'Inter, system-ui, sans-serif' }} data-testid="os-hub">
      <div className="max-w-5xl mx-auto px-5 py-10">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] font-bold" style={{ color: 'var(--os-accent)' }}>Kitxens OS</p>
            <h1 className="mt-2 text-[28px] lg:text-[34px] font-semibold tracking-tight" style={{ color: 'var(--os-ink)' }} data-testid="os-hub-greeting">
              {greet}, {me.name.split(' ')[0]}
            </h1>
            <p className="mt-1 text-[14px]" style={{ color: 'var(--os-ink-soft)' }}>
              {new Date().toLocaleDateString(isEs ? 'es-ES' : 'en-US', { weekday: 'long', month: 'long', day: 'numeric' })} · {isEs ? meta.es : meta.en}
            </p>
          </div>
          <div className="flex items-center gap-2.5">
            {me.weather && (
              <span className="hidden sm:inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12.5px] whitespace-nowrap flex-nowrap shrink-0"
                style={{ background: 'var(--os-surface)', border: '1px solid var(--os-border)', color: 'var(--os-ink-soft)' }}
                data-testid="os-hub-weather">
                <MapPin className="w-3.5 h-3.5 shrink-0" style={{ color: 'var(--os-accent)' }} aria-hidden /> {me.weather.city}
                <span className="inline-flex items-center gap-1 font-semibold whitespace-nowrap" style={{ color: 'var(--os-ink)' }} data-testid="os-hub-weather-temp">
                  {wIcon(me.weather.code)} {Math.round(me.weather.temp * 9 / 5 + 32)}°F<span className="font-normal" style={{ color: 'var(--os-ink-faint)' }}>&nbsp;·&nbsp;{Math.round(me.weather.temp)}°C</span>
                </span>
                {me.weather.days?.[1] && (
                  <span className="inline-flex items-center gap-1 pl-2 whitespace-nowrap" style={{ color: 'var(--os-ink-faint)', borderLeft: '1px solid var(--os-border)' }}>
                    {isEs ? 'mañana' : 'tmrw'} {wIcon(me.weather.days[1].code, 'w-3.5 h-3.5')} {Math.round(me.weather.days[1].max * 9 / 5 + 32)}°F&nbsp;·&nbsp;{Math.round(me.weather.days[1].max)}°C
                  </span>
                )}
              </span>
            )}
            <span className="hidden sm:inline-flex"><TextSizePill isEs={isEs} compact /></span>
            <LangSwitch />
            <ThemeToggle theme={theme} setTheme={setTheme} isEs={isEs} />
            <button onClick={logout} data-testid="os-hub-logout"
              className="inline-flex items-center gap-1.5 text-[13px] transition-colors hover:opacity-70" style={{ color: 'var(--os-ink-faint)' }}>
              <LogOut className="w-4 h-4" /> {isEs ? 'Salir' : 'Sign out'}
            </button>
          </div>
        </div>

        {roles.length > 1 && (
          <div className="mt-6 inline-flex items-center gap-1 rounded-full p-1" data-testid="os-role-switcher"
            style={{ background: 'var(--os-surface-2)', border: '1px solid var(--os-border)' }}>
            {roles.filter((r) => !(r === 'restaurant' && roles.includes('admin'))).map((r) => {
              const Icon = ROLE_META[r].icon;
              const active = role === r;
              return (
                <button key={r} onClick={() => setRole(r)} data-testid={`os-role-tab-${r}`}
                  className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[12.5px] font-bold transition-all"
                  style={{
                    background: active ? 'var(--os-surface)' : 'transparent',
                    color: active ? 'var(--os-accent)' : 'var(--os-ink-soft)',
                    boxShadow: active ? 'var(--os-shadow)' : 'none',
                  }}>
                  <Icon className="w-3.5 h-3.5" /> {isEs ? ROLE_META[r].es : ROLE_META[r].en}
                </button>
              );
            })}
          </div>
        )}

        <div className={`mt-6 grid grid-cols-2 gap-3 ${role === 'admin' ? 'lg:grid-cols-6' : ''}`}>
          <div className={role === 'admin' ? 'col-span-2 lg:col-span-2' : 'col-span-2'}>
            <HubConcierge role={role} isEs={isEs} />
          </div>
          {role === 'admin' && <AdminBriefRow admin={home?.admin} isEs={isEs} sel={briefSel} setSel={setBriefSel} />}
        </div>
        {role === 'admin' && briefSel && (
          <BriefDetail k={briefSel} admin={home?.admin} isEs={isEs} onEnterAdmin={() => setKwOpen(true)} />
        )}

        {err && <p className="mt-4 text-[13px] text-red-500" data-testid="os-hub-error">{err}</p>}

        {role === 'admin' && <AdminHome admin={home?.admin} isEs={isEs} onEnterAdmin={() => setKwOpen(true)} />}
        {role === 'restaurant' && <RestaurantHome me={me} home={home} isEs={isEs} enter={enter} busy={busy} />}
        {role === 'affiliate' && <AffiliateHome affiliate={home?.affiliate} isEs={isEs} enter={enter} />}
        {role === 'client' && <ClientHome client={home?.client} isEs={isEs} enter={enter} />}

        <div className="mt-8" data-testid="os-hub-workspaces">
          <p className="mb-3 text-[11px] uppercase tracking-[0.15em] font-bold" style={{ color: 'var(--os-ink-faint)' }}>
            {isEs ? 'Tus workspaces · elige dónde trabajar' : 'Your workspaces · choose where to work'}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {me.workspaces.map((w, i) => {
              const Icon = { restaurant: Store, admin: ShieldCheck, affiliate: Handshake, client: User }[w.type] || Store;
              const logo = w.type === 'admin' ? '/kitxens-logo.png' : w.logo;
              return (
                <button key={w.id} onClick={() => (w.requires_keyword ? setKwOpen(true) : enter(w))} disabled={busy === w.id}
                  data-testid={`os-ws-${w.id.replace(':', '-')}`}
                  draggable onDragStart={(e) => onDragStartWs(e, w)} onDragOver={(e) => e.preventDefault()} onDrop={(e) => onDropWs(e, w)}
                  title={isEs ? 'Arrastra para reordenar' : 'Drag to reorder'}
                  className={`group text-left rounded-2xl p-4 os3-lift transition-all disabled:opacity-60 cursor-grab active:cursor-grabbing ${i === 0 ? 'shadow-[0_0_0_1px_rgba(247,127,17,0.30),0_10px_28px_-14px_rgba(247,127,17,0.45)]' : ''}`}
                  style={{ background: 'var(--os-surface)', border: '1px solid var(--os-border)', borderTop: `3px solid ${w.color}`, animationDelay: `${i * 50}ms` }}>
                  <div className="flex items-center gap-3">
                    {logo
                      ? <img src={logo} alt="" className="w-11 h-11 rounded-full object-cover shrink-0"
                          style={w.type === 'admin' ? { background: `${w.color}14`, padding: 7 } : undefined} />
                      : <span className="w-11 h-11 rounded-full flex items-center justify-center shrink-0" style={{ background: `${w.color}18` }}><Icon style={{ color: w.color, width: 19, height: 19 }} /></span>}
                    <div className="min-w-0 flex-1">
                      <p className="text-[15px] font-semibold truncate flex items-center gap-1.5" style={{ color: 'var(--os-ink)' }}>
                        {w.type === 'admin' && <Star className="w-3.5 h-3.5 shrink-0" style={{ color: '#F77F11', fill: '#F77F11' }} data-testid="os-ws-admin-star" />}
                        {w.founding && <Star className="w-3.5 h-3.5 shrink-0 text-amber-400 fill-amber-400" data-testid="os-ws-founder-star" />}
                        <span className="truncate">{w.name}</span>
                        {w.id === lastWs && (
                          <span className="shrink-0 rounded-full border border-orange-200 bg-orange-50 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-[#d8651a]"
                            title={isEs ? 'Continúa donde lo dejaste' : 'Continue where you left off'} data-testid="os-ws-last-opened">
                            {isEs ? 'Continuar' : 'Continue'}
                          </span>
                        )}
                        {!lastWs && i === 0 && (
                          <span className="shrink-0 rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide"
                            style={{ borderColor: 'var(--os-border)', color: 'var(--os-ink-faint)' }} data-testid="os-ws-default-badge">
                            {isEs ? 'Predeterminado' : 'Default'}
                          </span>
                        )}
                      </p>
                      <p className="text-[12px] truncate" style={{ color: 'var(--os-ink-faint)' }}>
                        {w.type === 'restaurant' ? <>Restaurant AI OS · {w.os_id}{w.city ? ` · ${w.city}` : ''}</>
                          : w.type === 'admin' ? (isEs ? 'Operaciones y salud del sistema' : 'Operations & system health')
                            : w.type === 'affiliate' ? (isEs ? 'Referidos y comisiones' : 'Referrals & commissions')
                              : (isEs ? 'Tu área de cliente' : 'Your client area')}
                      </p>
                    </div>
                    {w.type !== 'admin' && (
                      <span role="button" tabIndex={0} onClick={(e) => hideWs(e, w)}
                        data-testid={`os-ws-hide-${w.id.replace(':', '-')}`} aria-label="Hide workspace"
                        title={isEs ? 'Ocultar de mi hub' : 'Hide from my hub'}
                        className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 opacity-30 hover:opacity-100 hover:bg-black/5 transition-all"
                        style={{ color: 'var(--os-ink-faint)' }}>
                        <X className="w-3.5 h-3.5" />
                      </span>
                    )}
                    <ArrowRight className="w-4 h-4 shrink-0 group-hover:translate-x-0.5 transition-transform" style={{ color: 'var(--os-ink-faint)' }} />
                  </div>
                </button>
              );
            })}
          </div>
          {(me.hidden_count || 0) > 0 && (
            <button onClick={unhideAll} data-testid="os-ws-unhide-all"
              className="mt-3 text-[11.5px] font-semibold transition-colors hover:opacity-70" style={{ color: 'var(--os-ink-faint)' }}>
              {isEs ? `Mostrar ${me.hidden_count} workspace${me.hidden_count > 1 ? 's' : ''} oculto${me.hidden_count > 1 ? 's' : ''}` : `Show ${me.hidden_count} hidden workspace${me.hidden_count > 1 ? 's' : ''}`}
            </button>
          )}
        </div>

        {me.workspaces.some((w) => w.type === 'admin') && (<>
          <VipInviteCard isEs={isEs} />
          <DedicationCard isEs={isEs} />
          <AwayRadar isEs={isEs} />
          <KeyLinksCard isEs={isEs} />
        </>)}

        <OsFooter isEs={isEs} />
        <div className="pb-4 -mt-6 text-center">
          <Link to="/" className="text-[11px] transition-colors hover:opacity-70" style={{ color: 'var(--os-ink-faint)' }}>kitxens.com</Link>
        </div>
      </div>

      {kwOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setKwOpen(false)}>
          <div className="rounded-2xl p-6 w-full max-w-sm" style={{ background: 'var(--os-surface)', border: '1px solid var(--os-border)' }}
            onClick={(e) => e.stopPropagation()} data-testid="os-hub-keyword-modal">
            <p className="text-[17px] font-semibold" style={{ color: 'var(--os-ink)' }}>{isEs ? 'Palabra de seguridad' : 'Security keyword'}</p>
            <p className="mt-1 text-[13.5px]" style={{ color: 'var(--os-ink-soft)' }}>
              {isEs ? 'Kitxens Admin requiere tu palabra clave — es una PALABRA, no un número. La define el equipo Kitxens.'
                : 'Kitxens Admin requires your keyword — it\u2019s a WORD, not a number. It\u2019s set by the Kitxens team.'}
            </p>
            <div className="relative mt-3">
              <input autoFocus type={showKw ? 'text' : 'password'} value={kw} onChange={(e) => { setKw(e.target.value); setKwErr(''); }} data-testid="os-hub-keyword-input"
                onKeyDown={(e) => e.key === 'Enter' && enter({ id: 'admin' }, kw)}
                className="w-full rounded-lg px-3 py-2.5 pr-11 text-[16px] uppercase tracking-widest focus:outline-none"
                style={{ background: 'var(--os-surface-2)', border: `1px solid ${kwErr ? '#ef4444' : 'var(--os-border)'}`, color: 'var(--os-ink)' }} placeholder="•••••" />
              <button type="button" onClick={() => setShowKw((v) => !v)} data-testid="os-hub-keyword-toggle"
                aria-label={showKw ? 'Hide' : 'Show'}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-full transition-opacity hover:opacity-70"
                style={{ color: 'var(--os-ink-faint)' }}>
                {showKw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {/* live guard: the guest is typing their PASSWORD, not the keyword */}
            {kw.length > 12 && /\d/.test(kw) && (
              <p className="mt-2 text-[12px] font-semibold text-amber-600" data-testid="os-hub-keyword-password-hint">
                {isEs ? 'Eso parece tu contraseña — la keyword es una PALABRA corta, sin números.'
                  : 'That looks like your password — the keyword is a short WORD, no numbers.'}
              </p>
            )}
            {kwErr && (
              <p className="mt-2 text-[12.5px] font-semibold text-red-500" data-testid="os-hub-keyword-error">
                {kwErr === 'invalid'
                  ? (isEs ? 'Keyword incorrecta. Es una PALABRA corta, no tu contraseña.' : 'Wrong keyword. It\u2019s a short WORD, not your password.')
                  : kwErr}
              </p>
            )}
            <button onClick={() => enter({ id: 'admin' }, kw)} data-testid="os-hub-keyword-submit"
              className="mt-3 w-full rounded-lg text-white font-bold text-[14.5px] py-3 transition-all hover:opacity-90"
              style={{ background: 'var(--os-accent)' }}>
              {isEs ? 'Entrar a Kitxens Admin' : 'Enter Kitxens Admin'}
            </button>
            <button type="button" data-testid="os-hub-keyword-reminder-btn" disabled={kwSent === 'busy'}
              onClick={async () => {
                setKwSent('busy');
                try {
                  const r = await fetch(`${API}/api/os/keyword-reminder`, { method: 'POST', credentials: 'include' });
                  setKwSent(r.ok ? 'sent' : 'fail');
                } catch { setKwSent('fail'); }
              }}
              className="mt-2.5 w-full text-[12px] font-semibold transition-colors hover:opacity-70 disabled:opacity-50"
              style={{ color: 'var(--os-ink-faint)' }}>
              {kwSent === 'sent'
                ? (isEs ? '✓ Enviada a tu email — revisa tu bandeja' : '✓ Sent to your email — check your inbox')
                : kwSent === 'busy' ? (isEs ? 'Enviando…' : 'Sending…')
                  : kwSent === 'fail' ? (isEs ? 'No se pudo enviar — escribe a support@kitxens.com' : 'Could not send — contact support@kitxens.com')
                    : (isEs ? '¿No recuerdas tu keyword? Recíbela en tu email' : 'Don\u2019t remember your keyword? Get it by email')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
